import { PublicClientApplication, AccountInfo } from "@azure/msal-browser";

let cachedTokenInfo: {
  token: string;
  expiresAt: number;
  refreshedAt: number;
} | null = null;
let logInterval: number | null = null;

const formatTimeRemaining = (expiresAt: number): string => {
  const remainingMs = expiresAt - Date.now();
  const remainingMinutes = Math.floor(remainingMs / 60000);
  const remainingSeconds = Math.floor((remainingMs % 60000) / 1000);
  return `${remainingMinutes}m ${remainingSeconds}s`;
};

const startTokenMonitoring = () => {
  // Clear existing interval
  if (logInterval) {
    clearInterval(logInterval);
  }

  // Log token status every 30 seconds and attempt proactive refresh
  logInterval = setInterval(() => {
    if (cachedTokenInfo) {
      const timeRemaining = formatTimeRemaining(cachedTokenInfo.expiresAt);
      const timeSinceRefresh = Date.now() - cachedTokenInfo.refreshedAt;
      const tokenAge = Math.floor(timeSinceRefresh / 60000); // minutes since refresh

      if (cachedTokenInfo.expiresAt <= Date.now()) {
        console.warn(`🔴 Token EXPIRED! Token was ${tokenAge} minutes old`);
      } else if (cachedTokenInfo.expiresAt <= Date.now() + 5 * 60 * 1000) {
        console.warn(
          `🟡 Token expiring soon! Time remaining: ${timeRemaining} | Token age: ${tokenAge}m`
        );
      } else if (cachedTokenInfo.expiresAt <= Date.now() + 15 * 60 * 1000) {
        // Proactive refresh when 15 minutes remaining to avoid iframe timeouts
        console.info(
          `🔄 Token will expire in ${timeRemaining}. Proactively clearing cache to trigger refresh on next request.`
        );
        cachedTokenInfo = null; // Clear cache to force refresh on next request
      } else {
        console.debug(
          `🟢 Token valid. Time remaining: ${timeRemaining} | Token age: ${tokenAge}m`
        );
      }
    }
  }, 30000) as unknown as number; // Log every 30 seconds
};

export const getAuthorizationToken = async (
  msalInstance: PublicClientApplication,
  account: AccountInfo | null,
  authority: string,
  logout: () => void
): Promise<string | null> => {
  // Check if we have a cached token that hasn't expired (with 5 minute buffer)
  if (
    cachedTokenInfo &&
    cachedTokenInfo.expiresAt > Date.now() + 5 * 60 * 1000
  ) {
    const timeRemaining = formatTimeRemaining(cachedTokenInfo.expiresAt);
    console.debug(
      `Using cached token. Expires: ${new Date(
        cachedTokenInfo.expiresAt
      ).toLocaleString()}, Time remaining: ${timeRemaining}`
    );
    return cachedTokenInfo.token;
  }

  if (cachedTokenInfo) {
    const timeRemaining = formatTimeRemaining(cachedTokenInfo.expiresAt);
    console.debug(
      `Cached token expired or expiring soon (${timeRemaining} remaining), requesting new token`
    );
  }

  const request = {
    scopes: [authority],
    forceRefresh: false, // Let MSAL handle token caching
  };

  try {
    console.debug("Acquiring token silently for scopes:", authority);
    const response = await msalInstance.acquireTokenSilent({
      ...request,
      account: account,
    });

    // Cache token with expiration info
    const now = Date.now();
    cachedTokenInfo = {
      token: response.accessToken,
      expiresAt: response.expiresOn
        ? response.expiresOn.getTime()
        : now + 60 * 60 * 1000, // 1 hour fallback
      refreshedAt: now,
    };

    const timeRemaining = formatTimeRemaining(cachedTokenInfo.expiresAt);
    console.info(
      `🔄 Token acquired successfully! Expires: ${new Date(
        cachedTokenInfo.expiresAt
      ).toLocaleString()}, Valid for: ${timeRemaining}`
    );

    // Start monitoring if this is a new token
    startTokenMonitoring();

    return cachedTokenInfo.token;
  } catch (error: any) {
    // Clear cached token on error
    cachedTokenInfo = null;

    console.error("Token acquisition failed:", error);

    // Handle specific MSAL errors
    if (error.name === "InteractionRequiredAuthError") {
      console.warn("🔄 Interaction required, redirecting to login");
      logout();
      window.location.href = "/login";
    } else if (
      error.errorCode === "monitor_window_timeout" ||
      error.name === "BrowserAuthError"
    ) {
      // Handle iframe timeout - common when user returns after being away
      console.warn(
        "⏱️ Silent token refresh timed out. User may need to sign in again."
      );

      // Log additional context for debugging
      console.warn("🔍 Timeout context:", {
        errorCode: error.errorCode,
        errorName: error.name,
        message: error.message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        cachedTokenWas: cachedTokenInfo
          ? {
              expired: cachedTokenInfo.expiresAt <= Date.now(),
              timeUntilExpiry: cachedTokenInfo.expiresAt - Date.now(),
              ageInMs: Date.now() - cachedTokenInfo.refreshedAt,
            }
          : "null",
      });

      // Show a user-friendly message
      if (
        typeof window !== "undefined" &&
        (window as any).showTokenTimeoutMessage
      ) {
        (window as any).showTokenTimeoutMessage();
      } else {
        // Fallback - Try to redirect to login instead of failing silently
        // Give user a brief moment to see what's happening
        setTimeout(() => {
          console.warn("🔄 Redirecting to login due to token refresh timeout");
          logout();
          window.location.href = "/login";
        }, 2000);
      }

      return null;
    } else {
      // For other errors, just log and return null - let the app handle gracefully
      console.error("🚨 Unexpected token acquisition error:", {
        name: error.name,
        errorCode: error.errorCode,
        message: error.message,
      });
    }

    return null;
  }
};

// Export function to clear cache (useful for logout or when token becomes invalid)
export const clearTokenCache = () => {
  console.info("🗑️ Clearing token cache and stopping monitoring");
  cachedTokenInfo = null;
  if (logInterval) {
    clearInterval(logInterval);
    logInterval = null;
  }
};

// Export function to get current token status (useful for debugging)
export const getTokenStatus = () => {
  if (!cachedTokenInfo) {
    return { status: "No token cached" };
  }

  const timeRemaining = formatTimeRemaining(cachedTokenInfo.expiresAt);
  const tokenAge = Math.floor(
    (Date.now() - cachedTokenInfo.refreshedAt) / 60000
  );
  const isExpired = cachedTokenInfo.expiresAt <= Date.now();
  const isExpiringSoon =
    cachedTokenInfo.expiresAt <= Date.now() + 5 * 60 * 1000;

  return {
    status: isExpired ? "Expired" : isExpiringSoon ? "Expiring Soon" : "Valid",
    expiresAt: new Date(cachedTokenInfo.expiresAt).toLocaleString(),
    timeRemaining,
    tokenAge: `${tokenAge}m`,
    isExpired,
    isExpiringSoon,
  };
};
